import React from 'react';
import styled from 'styled-components';
import { FormValues } from '.';
import {
    Box,
    Button,
    ButtonProps,
    Checkbox,
    CheckboxProps,
    Text,
    TextField,
    TextFieldProps,
    TextProps,
} from '../../atoms';
import { FormField } from '../../molecules';
import { Form } from '../../organisms';
import { FormErrors } from './hooks';

interface LoginFormProps extends React.HTMLAttributes<HTMLFormElement> {
    formHeader?: TextProps;
    values?: FormValues;
    errors?: FormErrors;
    usernameField?: string | TextFieldProps;
    passwordField?: string | TextFieldProps;
    loginButton?: string | ButtonProps;
    rememberMeCheckbox?: string | CheckboxProps;
}

const LoginFormWrapper = styled.div`
    width: calc(100% - 1.25rem);
    max-width: 1120px;
    margin: 0 auto;

    .o-form {
        margin: 1.25rem;
    }

    .o-form button[type='submit'] {
        margin: 0;
    }

    .a-text {
        margin-bottom: 1.25rem;
    }

    .a-checkbox {
        margin: 1.25rem 0;
    }
`;

export const FIELD_USERNAME = 'username';
export const FIELD_PASSWORD = 'password';

const LoginForm = React.forwardRef<HTMLFormElement, LoginFormProps>(
    (
        {
            formHeader,
            values,
            errors,
            passwordField,
            usernameField,
            rememberMeCheckbox,
            loginButton,
            ...rest
        },
        ref,
    ) => {
        const { username, password } = values || {};
        const Username =
            typeof usernameField === 'object' ? (
                <TextField
                    id="login-username"
                    type="text"
                    name={FIELD_USERNAME}
                    value={username}
                    {...usernameField}
                />
            ) : (
                <TextField
                    id="login-username"
                    label={usernameField || 'Username'}
                    type="text"
                    name={FIELD_USERNAME}
                    value={username}
                />
            );
        const Password =
            typeof passwordField === 'object' ? (
                <TextField
                    id="login-password"
                    type="password"
                    name={FIELD_PASSWORD}
                    value={password}
                    {...passwordField}
                />
            ) : (
                <TextField
                    id="login-password"
                    type="password"
                    label={passwordField || 'Password'}
                    name={FIELD_PASSWORD}
                    value={password}
                />
            );
        const RememberMe =
            typeof rememberMeCheckbox === 'object' ? (
                <Checkbox id="login-remember" {...rememberMeCheckbox} />
            ) : (
                <Checkbox
                    id="login-remember"
                    label={rememberMeCheckbox || 'Stay logged in'}
                />
            );

        const LoginButton =
            typeof loginButton === 'object' ? (
                <Button type="submit" {...loginButton} />
            ) : (
                <Button
                    aria-label={loginButton || 'Login'}
                    label={loginButton || 'Login'}
                    type="submit"
                />
            );

        return (
            <LoginFormWrapper>
                <Form description="Login form" ref={ref} {...rest}>
                    {formHeader && <Text {...formHeader} />}
                    <FormField
                        fieldType="text"
                        notificationText={errors?.username?.text}
                        notificationType={errors?.username?.type}
                    >
                        {Username}
                    </FormField>
                    <FormField
                        fieldType="password"
                        notificationText={errors?.password?.text}
                        notificationType={errors?.password?.type}
                    >
                        {Password}
                    </FormField>
                    {RememberMe}
                    {LoginButton}
                </Form>
            </LoginFormWrapper>
        );
    },
);

LoginForm.displayName = 'LoginForm';

export { LoginForm, LoginFormProps };
